/**
 * Watchlist Follows Hooks - React Query Adapter
 *
 * Phase 3.1: Single source of truth for watchlist follow state.
 * React Query IS the cache - no bespoke state.
 *
 * Features:
 * - useFollowedWatchlists(): Query for user's followed lists
 * - useWatchlistFollowStatus(): Query for single watchlist follow status
 * - useToggleWatchlistFollow(): Mutation with optimistic update
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { queryKeys } from '../lib/queryKeys';
import { apiFetch, ApiError } from '../lib/api';

// ============================================================================
// Types
// ============================================================================

export interface FollowedWatchlist {
  id: string;
  name: string;
  description: string | null;
  item_count: number;
  follower_count: number;
  media_filter: 'movie' | 'tv' | 'mixed';
  followed_at: string;
  owner: {
    id: string;
    username: string;
    avatar_url: string | null;
  };
}

// Stable empty array reference to prevent infinite re-render loops
// when query.data is undefined (new users, deactivated users, etc.)
const EMPTY_FOLLOWED_LISTS: FollowedWatchlist[] = [];

interface FollowedWatchlistsResponse {
  watchlists: FollowedWatchlist[];
}

// ============================================================================
// useFollowedWatchlists - Query hook for user's followed lists
// ============================================================================

interface UseFollowedWatchlistsResult {
  followedLists: FollowedWatchlist[];
  isLoading: boolean;
  error: Error | null;
}

// LocalStorage cache key and TTL for followed lists
const FOLLOWED_LISTS_CACHE_PREFIX = 'filmple:followedLists:';
const FOLLOWED_LISTS_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

interface CachedFollowedLists {
  lists: FollowedWatchlist[];
  timestamp: number;
}

function getFollowedListsFromCache(userId: string): CachedFollowedLists | null {
  try {
    const raw = localStorage.getItem(`${FOLLOWED_LISTS_CACHE_PREFIX}${userId}`);
    if (!raw) return null;
    const cached = JSON.parse(raw) as CachedFollowedLists;
    // Check TTL
    if (Date.now() - cached.timestamp > FOLLOWED_LISTS_CACHE_TTL_MS) {
      localStorage.removeItem(`${FOLLOWED_LISTS_CACHE_PREFIX}${userId}`);
      return null;
    }
    return cached;
  } catch {
    return null;
  }
}

function setFollowedListsCache(userId: string, lists: FollowedWatchlist[]): void {
  try {
    const cached: CachedFollowedLists = { lists, timestamp: Date.now() };
    localStorage.setItem(`${FOLLOWED_LISTS_CACHE_PREFIX}${userId}`, JSON.stringify(cached));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Hook to get all watchlists the user is following.
 * Single source of truth for Sidebar and WatchlistPage.
 *
 * FIX 2: Cache-first from localStorage for instant render on refresh.
 */
export function useFollowedWatchlists(): UseFollowedWatchlistsResult {
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;

  // Get cached data for initialData (cache-first pattern)
  const cached = userId ? getFollowedListsFromCache(userId) : null;

  const query = useQuery({
    queryKey: queryKeys.watchlist.followed(userId || ''),
    queryFn: async () => {
      if (!token) return [];

      const data = await apiFetch<FollowedWatchlistsResponse>('/watchlists/followed', {
        token,
      });
      const lists = data.watchlists || [];

      // Update localStorage cache on success
      if (userId) {
        setFollowedListsCache(userId, lists);
      }

      return lists;
    },
    // Only fetch if user is logged in
    enabled: !!userId && !!token,
    // Keep data fresh for 60 seconds
    staleTime: 60_000,
    // Use cached data as initial data for instant render
    initialData: cached?.lists,
    initialDataUpdatedAt: cached?.timestamp,
  });

  return {
    followedLists: query.data ?? EMPTY_FOLLOWED_LISTS,
    isLoading: query.isLoading,
    error: query.error as Error | null,
  };
}

// ============================================================================
// useWatchlistFollowStatus - Query hook for single watchlist follow status
// ============================================================================

interface UseWatchlistFollowStatusResult {
  isFollowing: boolean;
  isLoading: boolean;
}

/**
 * Hook to check if user is following a specific watchlist.
 * Derives from the followed lists cache - no extra network call.
 */
export function useWatchlistFollowStatus(watchlistId: string | undefined): UseWatchlistFollowStatusResult {
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;
  const queryClient = useQueryClient();
  const followedLists = queryClient.getQueryData<FollowedWatchlist[]>(
    queryKeys.watchlist.followed(userId || '')
  );

  // If no userId/token/watchlistId, not following
  if (!userId || !token || !watchlistId) {
    return { isFollowing: false, isLoading: false };
  }

  // Check if watchlistId is in followed lists
  const isFollowing = followedLists?.some((list) => list.id === watchlistId) ?? false;

  // Loading if followed lists query hasn't loaded yet
  const followedQuery = queryClient.getQueryState(queryKeys.watchlist.followed(userId));
  const isLoading = followedQuery?.status === 'pending';

  return {
    isFollowing,
    isLoading,
  };
}

// ============================================================================
// useToggleWatchlistFollow - Mutation hook for follow/unfollow
// ============================================================================

/** Optional owner info for optimistic placeholder rendering */
export interface ToggleFollowOwnerInfo {
  id?: string;
  username?: string;
  avatar_url?: string | null;
}

interface ToggleFollowVariables {
  watchlistId: string;
  watchlistName: string;
  isCurrentlyFollowing: boolean;
  owner?: ToggleFollowOwnerInfo;
  media_filter?: 'movie' | 'tv' | 'mixed';
}

interface UseToggleWatchlistFollowResult {
  toggleFollow: (
    watchlistId: string,
    watchlistName: string,
    isCurrentlyFollowing: boolean,
    opts?: { owner?: ToggleFollowOwnerInfo; media_filter?: 'movie' | 'tv' | 'mixed' }
  ) => Promise<boolean | 'SOCIAL_TIER_REQUIRED'>;
  isLoading: boolean;
}

/**
 * Hook to toggle follow/unfollow on a watchlist.
 * Uses optimistic updates with rollback on error.
 */
export function useToggleWatchlistFollow(): UseToggleWatchlistFollowResult {
  const { user, session } = useAuth();
  const userId = user?.id;
  const token = session?.access_token;
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (variables: ToggleFollowVariables) => {
      if (!token) throw new Error('Not authenticated');

      await apiFetch(`/watchlists/${variables.watchlistId}/follow`, {
        token,
        method: variables.isCurrentlyFollowing ? 'DELETE' : 'POST',
      });

      return !variables.isCurrentlyFollowing; // New follow state
    },

    onMutate: async (variables) => {
      if (!userId) return;

      const followedKey = queryKeys.watchlist.followed(userId);

      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: followedKey });

      // Snapshot previous value
      const previousFollowed = queryClient.getQueryData<FollowedWatchlist[]>(followedKey);

      // Optimistically update
      if (variables.isCurrentlyFollowing) {
        // Remove from followed list
        queryClient.setQueryData<FollowedWatchlist[]>(followedKey, (old) =>
          old?.filter((list) => list.id !== variables.watchlistId) ?? []
        );
      } else {
        // Add to followed list with owner info for proper Sidebar rendering
        queryClient.setQueryData<FollowedWatchlist[]>(followedKey, (old) => [
          ...(old ?? []),
          {
            id: variables.watchlistId,
            name: variables.watchlistName,
            description: null,
            item_count: 0,
            follower_count: 0,
            media_filter: variables.media_filter ?? 'mixed',
            followed_at: new Date().toISOString(),
            owner: {
              id: variables.owner?.id ?? '',
              username: variables.owner?.username ?? '',
              avatar_url: variables.owner?.avatar_url ?? null,
            },
          },
        ]);
      }

      // Return context for rollback
      return { previousFollowed, followedKey };
    },

    onError: (_error, _variables, context) => {
      // Rollback to previous value on error
      if (context?.followedKey && context.previousFollowed !== undefined) {
        queryClient.setQueryData(context.followedKey, context.previousFollowed);
      }
    },

    onSuccess: (_data, _variables, context) => {
      // Invalidate to get fresh data from server (with full owner info etc.)
      if (context?.followedKey) {
        queryClient.invalidateQueries({ queryKey: context.followedKey });
      }
    },
  });

  const toggleFollow = async (
    watchlistId: string,
    watchlistName: string,
    isCurrentlyFollowing: boolean,
    opts?: { owner?: ToggleFollowOwnerInfo; media_filter?: 'movie' | 'tv' | 'mixed' }
  ): Promise<boolean | 'SOCIAL_TIER_REQUIRED'> => {
    if (!userId || !token) return false;

    try {
      await mutation.mutateAsync({
        watchlistId,
        watchlistName,
        isCurrentlyFollowing,
        owner: opts?.owner,
        media_filter: opts?.media_filter,
      });
      return true;
    } catch (error) {
      console.error('Error toggling watchlist follow:', error);
      if (error instanceof ApiError && error.code === 'SOCIAL_TIER_REQUIRED') {
        return 'SOCIAL_TIER_REQUIRED';
      }
      return false;
    }
  };

  return {
    toggleFollow,
    isLoading: mutation.isPending,
  };
}
